import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

export default class extends Controller {
  static targets = ["select", "site", "zip"]
  static values = { 
    create: Boolean,
    url: String,
    createUrl: String
  }
  
  // Cache für geladene Optionen
  loadedOptions = new Map()
  
  // Debounce-Timer
  searchTimeout = null

  connect() {
    this.initializeTomSelect()
  }

  disconnect() {
    if (this.tomSelect) {
      this.tomSelect.destroy()
    }
  }

  initializeTomSelect() {
    if (!this.hasSelectTarget) {
      console.error('Select target not found')
      return
    }
    
    const options = {
      valueField: 'id',
      labelField: 'title',
      searchField: ['title', 'city', 'zip'],
      create: this.createValue,
      createOnBlur: false,
      createFilter: () => false, // Deaktivieren der Standard-Erstellung
      allowEmptyOption: true, // Erlaube leere Optionen (z.B. "Ohne Standort")
      load: (query, callback) => {
        // Debounce: Warte 600ms nach dem letzten Tastendruck
        clearTimeout(this.searchTimeout)
        
        this.searchTimeout = setTimeout(() => {
          this.loadOptions(query, callback)
        }, 600)
      },
      shouldLoad: (query) => {
        // Nur laden wenn der Query mindestens 3 Zeichen hat
        return query.length >= 3
      },
      onLoad: (data) => {
      },
      onDropdownOpen: () => {
      },
              render: {
          option: (item, escape) => {
            console.log('Rendering option:', item)
            
            // Einfache Version für Debugging
            if (item.type === 'maps') {
              return `<div class="maps-result p-2 border-l-4 border-blue-500 bg-blue-50 rounded">
                <div class="font-semibold text-gray-900 mb-1">${escape(item.title || 'Kein Titel')}</div>
                <div class="text-sm text-gray-700">
                  ${escape(item.city || 'Keine Stadt')} • ${escape(item.zip || 'Keine PLZ')}
                  ${item.region ? ` • ${escape(item.region)}` : ''}
                </div>
                <div class="text-xs text-blue-600 mt-1">Maps-API - Neuer Einsatzort</div>
              </div>`
            } else {
              return `<div class="existing-jobsite p-2 border-l-4 border-green-500 bg-green-50 rounded">
                <div class="font-semibold text-gray-900 mb-1">${escape(item.title || 'Kein Titel')}</div>
                <div class="text-sm text-gray-700">
                  ${escape(item.city || 'Keine Stadt')} • ${escape(item.zip || 'Keine PLZ')}
                  ${item.region ? ` • ${escape(item.region)}` : ''}
                </div>
                <div class="text-xs text-green-600 mt-1">Bestehender Einsatzort</div>
              </div>`
            }
          },
        item: (item, escape) => {
          console.log('Rendering item:', item)
          
          // Sammle alle verfügbaren Informationen
          const parts = []
          
          if (item.title) parts.push(escape(item.title))
          if (item.city) parts.push(escape(item.city))
          if (item.zip) parts.push(escape(item.zip))
          if (item.region) parts.push(escape(item.region))
          
          // Erstelle den Untertitel nur wenn Informationen vorhanden sind
          if (parts.length > 0) {
            return `<div>${parts.join(' - ')}</div>`
          } else {
            return `<div>${escape(item.title || 'Unbekannter Einsatzort')}</div>`
          }
        }
      },
      onChange: (value) => {
        console.log('TomSelect onChange triggered with value:', value)
        this.handleSelection(value)
        
        // Stelle sicher, dass der Wert im Select gesetzt wird
        if (value && this.selectTarget.value !== value) {
          this.selectTarget.value = value
          // Event auslösen, damit Rails das Formular korrekt verarbeitet
          this.selectTarget.dispatchEvent(new Event('change', { bubbles: true }))
        }
        
        // Auto-fill site and zip fields
        this.autoFillSiteAndZip(value)
      }
    }

    this.tomSelect = new TomSelect(this.selectTarget, options)

    // Beim Bearbeiten: Einsatzort-Beschreibung und PLZ aus bereits gewähltem Einsatzort füllen
    const initialValue = this.tomSelect.getValue()
    if (initialValue && (this.hasSiteTarget || this.hasZipTarget)) {
      this.autoFillSiteAndZip(initialValue)
    }
  }

  async loadOptions(query, callback) {
    if (query.length < 3) {
      callback()
      return
    }

    // Prüfe Cache zuerst
    if (this.loadedOptions.has(query)) {
      console.log('Using cached options for query:', query)
      const cachedOptions = this.loadedOptions.get(query)
      this.addOptionsToTomSelect(cachedOptions)
      callback(cachedOptions)
      return
    }

    console.log('Loading options for query:', query)

    try {
      const url = this.urlValue.replace('QUERY', encodeURIComponent(query))
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      console.log('Response status:', response.status)

      if (response.ok) {
        const data = await response.json()
        console.log('Response data:', data)
        
        const options = []

        // Bestehende Einsatzorte hinzufügen
        if (data.existing && Array.isArray(data.existing)) {
          data.existing.forEach(item => {
            console.log('Processing existing jobsite:', item)
            if (item.title && item.city && item.zip !== undefined) {
              options.push({
                id: item.id,
                title: item.title,
                city: item.city,
                zip: item.zip,
                region: item.region || null,
                type: 'existing'
              })
            } else {
              console.warn('Skipping existing jobsite with missing data:', item)
            }
          })
        } else {
          console.log('No existing jobsites found or invalid format:', data.existing)
        }

        // Maps-Ergebnisse hinzufügen
        if (data.maps && Array.isArray(data.maps)) {
          data.maps.forEach(item => {
            console.log('Processing maps result:', item)
            if (item.title && item.city && item.zip !== undefined) {
              const mapsOption = {
                id: `maps_${Date.now()}_${Math.random()}`, // Temporäre ID für Maps-Ergebnisse
                title: item.title,
                city: item.city,
                zip: item.zip,
                region: item.region || null,
                country: item.country || 'Deutschland',
                type: 'maps'
              }
              console.log('Created maps option:', mapsOption)
              options.push(mapsOption)
            } else {
              console.warn('Skipping maps result with missing data:', item)
            }
          })
        }

        // Priorisierung: Orte/Städte vor Straßen und POIs
        options.sort((a, b) => {
          const getPriority = (option) => {
            const title = option.title.toLowerCase()
            
            // Höchste Priorität: Städte/Orte (nur Stadtname, keine Straße)
            if (title === option.city.toLowerCase()) {
              return 1
            }
            
            // Mittlere Priorität: Orte mit Region/Staat
            if (title.includes(option.city.toLowerCase()) && 
                (title.includes(option.region?.toLowerCase() || '') || 
                 title.includes(option.country?.toLowerCase() || ''))) {
              return 2
            }
            
            // Niedrigere Priorität: Straßen (enthalten typische Straßenwörter)
            const streetKeywords = ['straße', 'str.', 'weg', 'allee', 'platz', 'ring', 'boulevard', 'avenue', 'road', 'street']
            if (streetKeywords.some(keyword => title.includes(keyword))) {
              return 4
            }
            
            // Niedrigste Priorität: POIs und andere (enthalten typische POI-Wörter)
            const poiKeywords = ['restaurant', 'hotel', 'cafe', 'bar', 'shop', 'store', 'bank', 'apotheke', 'krankenhaus', 'schule', 'museum', 'park', 'bahnhof', 'station']
            if (poiKeywords.some(keyword => title.includes(keyword))) {
              return 5
            }
            
            // Standard-Priorität für unbekannte Typen
            return 3
          }
          
          return getPriority(a) - getPriority(b)
        })
        
        console.log('Processed options (sorted by priority):', options)
        console.log('Calling callback with options')
        
        // Fallback: Wenn keine Optionen gefunden wurden, zeige eine Nachricht
        if (options.length === 0) {
          options.push({
            id: 'no_results',
            title: 'Keine Ergebnisse gefunden',
            city: '',
            zip: '',
            type: 'no_results'
          })
        }
        
        // Debug: Überprüfe jede Option vor dem Callback
        options.forEach((option, index) => {
          console.log(`Option ${index}:`, {
            id: option.id,
            title: option.title,
            city: option.city,
            zip: option.zip,
            region: option.region,
            type: option.type
          })
        })
        
        // Cache die Optionen
        this.loadedOptions.set(query, options)
        
        // Manuell alle Optionen zu TomSelect hinzufügen
        this.addOptionsToTomSelect(options)
        
        // Dann den Callback aufrufen
        callback(options)
      } else {
        console.error('Fehler beim Laden der Einsatzorte:', response.statusText)
        const errorText = await response.text()
        console.error('Error response:', errorText)
        callback()
      }
    } catch (error) {
      console.error('Fehler beim Laden der Einsatzorte:', error)
      callback()
    }
  }

  async handleSelection(value) {
    console.log('handleSelection called with value:', value)
    if (!value) return

    const selectedOption = this.tomSelect.options[value]
    console.log('Selected option:', selectedOption)
    
    if (selectedOption && selectedOption.type === 'maps') {
      // Bestätigung für neue Einsatzorte anfordern
      const confirmed = await this.confirmNewJobsite(selectedOption)
      
      if (confirmed) {
        try {
          const response = await fetch('/de/backend/jobsites/create_from_maps', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
            },
            body: JSON.stringify({ 
              maps_data: {
                title: selectedOption.title,
                city: selectedOption.city,
                zip: selectedOption.zip,
                country: selectedOption.country || 'Deutschland'
              }
            })
          })

          if (response.ok) {
            const result = await response.json()
            
            if (result.success) {
              // Neuen Einsatzort zur Liste hinzufügen
              this.tomSelect.addOption({
                id: result.jobsite.id,
                title: result.jobsite.title,
                city: result.jobsite.city,
                zip: result.jobsite.zip,
                type: 'existing'
              })
              
              // Neuen Einsatzort auswählen
              this.tomSelect.setValue(result.jobsite.id)
              
              // Auto-fill site and zip fields für den neuen Einsatzort
              this.autoFillSiteAndZip(result.jobsite.id)
              
              // Erfolgsmeldung anzeigen
              this.showNotification('Einsatzort erfolgreich erstellt!', 'success')
            } else {
              this.showNotification('Fehler beim Erstellen des Einsatzorts: ' + result.errors.join(', '), 'error')
              this.tomSelect.clear()
            }
          } else {
            this.showNotification('Fehler beim Erstellen des Einsatzorts', 'error')
            this.tomSelect.clear()
          }
        } catch (error) {
          console.error('Fehler beim Erstellen des Einsatzorts:', error)
          this.showNotification('Fehler beim Erstellen des Einsatzorts', 'error')
          this.tomSelect.clear()
        }
      } else {
        // Auswahl zurücksetzen
        this.tomSelect.clear()
      }
    }
  }

  async confirmNewJobsite(jobsiteData) {
    const regionText = jobsiteData.region ? `\nRegion: ${jobsiteData.region}` : ''
    const message = `Möchten Sie einen neuen Einsatzort erstellen?\n\n` +
                   `Titel: ${jobsiteData.title}\n` +
                   `Stadt: ${jobsiteData.city}\n` +
                   `PLZ: ${jobsiteData.zip}${regionText}\n\n` +
                   `Dieser Einsatzort wird in der Datenbank gespeichert.`

    return confirm(message)
  }

  addOptionsToTomSelect(options) {
    console.log('Adding options to TomSelect:', options)
    
    // Alle bestehenden Optionen löschen (außer der aktuell ausgewählten)
    const currentValue = this.tomSelect.getValue()
    this.tomSelect.clearOptions()
    
    // Neue Optionen hinzufügen
    options.forEach(option => {
      this.tomSelect.addOption(option)
    })
    
    // Aktuellen Wert wiederherstellen
    if (currentValue) {
      this.tomSelect.setValue(currentValue)
    }
    
    console.log('Options after adding to TomSelect:', this.tomSelect.options)
  }

  showNotification(message, type) {
    // Einfache Benachrichtigung (kann durch Toast-Notification ersetzt werden)
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 p-4 rounded-md shadow-lg z-50 ${
      type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
    }`
    notification.textContent = message
    
    document.body.appendChild(notification)
    
    setTimeout(() => {
      notification.remove()
    }, 5000)
  }

  async autoFillSiteAndZip(jobsiteId) {
    if (!jobsiteId) return

    // Keine Auto-Befüllung für Maps-Temporär-IDs oder "no_results"
    const numericId = typeof jobsiteId === 'string' ? jobsiteId.replace(/^\D+/, '') : jobsiteId
    if (String(jobsiteId).startsWith('maps_') || jobsiteId === 'no_results' || !numericId) return

    let title = null
    let zip = null

    const selectedOption = this.tomSelect.options[jobsiteId]
    const fromSearchOrCreate = selectedOption && (selectedOption.type === 'existing' || selectedOption.type === 'maps')
    if (fromSearchOrCreate && selectedOption.title) {
      // Daten aus Suche oder neu erstelltem Einsatzort: sauberer Titel und PLZ
      title = selectedOption.title
      if (selectedOption.zip !== undefined && selectedOption.zip !== null && selectedOption.zip !== '') {
        zip = selectedOption.zip
      }
    } else {
      // Initiale Auswahl aus dem Dropdown: Jobsite-Daten per API laden (Name + PLZ)
      const jobsiteData = await this.fetchJobsiteById(jobsiteId)
      if (jobsiteData) {
        title = jobsiteData.title
        zip = jobsiteData.zip
      }
    }

    if (!title && !zip) return

    // Einsatzort-Beschreibung (site) mit Namen des Einsatzortes füllen
    if (this.hasSiteTarget && title) {
      this.siteTarget.value = title
      this.siteTarget.dispatchEvent(new Event('input', { bubbles: true }))
      this.siteTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }

    // PLZ füllen, wenn beim Einsatzort hinterlegt
    if (this.hasZipTarget && zip) {
      this.zipTarget.value = zip
      this.zipTarget.dispatchEvent(new Event('input', { bubbles: true }))
      this.zipTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  async fetchJobsiteById(jobsiteId) {
    const baseUrl = this.urlValue.split('?')[0].replace(/\/search$/, '')
    const apiUrl = `${baseUrl}/api_show/${encodeURIComponent(jobsiteId)}`
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      })
      if (response.ok) {
        return await response.json()
      }
    } catch (e) {
      console.warn('Jobsite-Daten konnten nicht geladen werden:', e)
    }
    return null
  }
}
